import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Outlet} from 'react-router-dom'; //NOTE: Outlet Required see: https://reactrouter.com/en/main/components/outlet
import "es6-string-polyfills";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Alert from "@mui/material/Alert";
import axios from "axios";
import './App.css';
import style from "./adminTheme";
import {openDrawer} from './actions/drawerActions';
import {getSystemSetting} from "./actions/systemSettingActions";
import {logout} from "./actions/loginActions";
import {getActionTypesList} from "./actions/questionnaireActionTypes";
import HelpDrawer, {helpProps} from "../src/components/drawers/HelpDrawerHook";
import ArgoAppBarHook from "./components/common/ArgoAppBarHook"
import ArgoAboutDialogHook from "./components/common/ArgoAboutDialogHook"
import ArgoLeftNavLinkHook from "./components/common/ArgoLeftNavLinkHook"
import helpMap from "./utilities/helpMap"; // An array of main menu page names
import {deriveDisplayVersion} from "./utilities/versionUtils";
import SetPortalUrlHook from "./portal/utilities/SetPortalUrlHook"
import {QD_HELP_URL, QD_PORTAL, QD_URI_PREFIX} from "./constants/systemConstants";
import {REACT_APP_PORTAL_MODE} from "./constants/systemConstants";
import {RELEASE_NAME, GIT_BRANCH} from "./constants/buildConstants";
import {debugLog} from "./utilities/debugLogger";


const App = (props) => {
    const [leftNavDrawerOpen, setLeftNavDrawerOpen] = useState(false);
    const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
    const [versionWarning, setVersionWarning] = useState(null); // {severity, message}
    const [backendVersionInfo, setBackendVersionInfo] = useState(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const settingsLoaded = useSelector(state => state.settings && state.settings.loaded);
    const drawerStack = useSelector(state => state.drawerStack)

    const {location, portalMode = "false"} = props;

    useEffect(() => {
        debugLog('APP','Constructor/Mount - portalMode=' + portalMode + ' path=' + (location && location.pathname));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        debugLog('APP','componentDidMount');
        if (REACT_APP_PORTAL_MODE === "false") {
            if (!settingsLoaded) {
                debugLog('APP','Triggering initial data fetch (didMount guarded)');
                dispatch(getSystemSetting());
            } else {
                debugLog('APP','Settings already loaded; skipping fetch');
            }
            dispatch(getActionTypesList());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            debugLog('APP','componentWillUnmount');
        };
    }, []);

    // Fetch backend version info and check for mismatch
    useEffect(() => {
        if (REACT_APP_PORTAL_MODE === "true") return;

        const frontendVersion = deriveDisplayVersion(RELEASE_NAME, GIT_BRANCH);

        axios.get(QD_URI_PREFIX + "/management/health", {timeout: 10000})
            .then(response => {
                const appVersion = response.data?.components?.appVersion?.details;
                if (appVersion) {
                    setBackendVersionInfo(appVersion);
                    const backendVersion = appVersion.displayVersion || "";
                    if (backendVersion && frontendVersion && backendVersion !== frontendVersion
                        && frontendVersion !== "local dev" && backendVersion !== "local dev") {
                        setVersionWarning({
                            severity: "warning",
                            message: "Version mismatch \u2014 Frontend: " + frontendVersion + " / Backend: " + backendVersion
                        });
                    }
                }
            })
            .catch(() => {
                setVersionWarning({
                    severity: "error",
                    message: "Backend unavailable \u2014 could not verify version"
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleLeftNavDrawer = useCallback(() => {
        setLeftNavDrawerOpen(prev => !prev);
    }, []);

    const closeLeftNavDrawer = useCallback(() => {
        setLeftNavDrawerOpen(false);
    }, []);

    const openHelpTab = useCallback(() => {
        window.open(QD_HELP_URL);
    }, []);

    const openAboutDialog = useCallback(() => {
        setAboutDialogOpen(true);
    }, []);

    const closeAboutDialog = useCallback(() => {
        setAboutDialogOpen(false);
    }, []);

    const openHelpDrawer = useCallback(() => {

        let pathName = location.pathname;

        // remove trailing slash
        if (pathName.charAt(pathName.length-1) === "/") {
            pathName = pathName.slice(0, -1);
        }

        let page = pathName.substring(pathName.lastIndexOf('/') +1);
        if (!page) page = "questionnaires";

        let path = helpMap.buildPath(page);

        dispatch(openDrawer(helpProps, <HelpDrawer src={path}/>));
    }, [location, dispatch]);

    const closeVersionsPage = useCallback((pathname) => {
        let path = "";

        if (pathname.startsWith("/qd2admin/questionnaire-versions")) {
            path = "/qd2admin/questionnaires";
        } else if (pathname.startsWith("/qd2admin/category-versions")) {
            path = "/qd2admin/categories";
        } else if (pathname.startsWith("/qd2admin/group-versions")) {
            path = "/qd2admin/groups";
        } else if (pathname.startsWith("/qd2admin/question-versions")) {
            path = "/qd2admin/questions";
        } else if (pathname.startsWith("/qd2admin/action-versions")) {
            path = "/qd2admin/actions";
        } else if (pathname.startsWith("/qd2admin/integration-logs")) {
            path = "/qd2admin/integrations";
        }

        navigate(path, {replace: true});
    }, [navigate]);

    const displayCorrectArgoAppBar = (() => {
        let pathname = location.pathname;

        if ((pathname.startsWith("/qd2admin/questionnaire-versions")) ||
            (pathname.startsWith("/qd2admin/category-versions")) ||
            (pathname.startsWith("/qd2admin/group-versions")) ||
            (pathname.startsWith("/qd2admin/question-versions")) ||
            (pathname.startsWith("/qd2admin/action-versions")) ||
            (pathname.startsWith("/qd2admin/integration-logs")))
        {
            return <ArgoAppBarHook onLeftIconButtonTouchTap={() => closeVersionsPage(pathname)} showMenuArrowButton="LEFT" childPage={true}/>;
        }
        else if (pathname.startsWith("/qd2admin/questionnaire-editor")) {
            if (pathname.slice(-1) !== "1") {
                return <ArgoAppBarHook title={drawerStack.displayTitle} showMenuIconButton={false} noIcon={true} isDrawer={true}/>
            }
            else {
                return <ArgoAppBarHook title={"Clone Questionnaire"} showMenuIconButton={false} noIcon={true} isDrawer={true}/>
            }
        }
        else {
            return <ArgoAppBarHook showMenuIconButton={true} onLeftIconButtonTouchTap={toggleLeftNavDrawer} showHelpIconButton={true} onRightIconButtonTouchTap={openHelpDrawer}/>;
        }
    });

    const handleLogout = useCallback(() => {
        dispatch(logout());
    }, [dispatch]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                closeLeftNavDrawer()
            }
        }

        document.addEventListener("keydown", handleKeyDown, true)

        return () => {document.removeEventListener("keydown", handleKeyDown, true)}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="App">
            {portalMode === "true" ?
                <div>
                    <SetPortalUrlHook url={QD_PORTAL}/>
                </div>
                :
                <div>
                    {versionWarning &&
                        <Alert severity={versionWarning.severity} sx={{borderRadius: 0}}>
                            {versionWarning.message}
                        </Alert>
                    }
                    {displayCorrectArgoAppBar()}
                    {leftNavDrawerOpen ?
                        <Drawer
                            open={leftNavDrawerOpen}
                            PaperProps={{sx: {width: style.leftNav.width, backgroundColor: style.paper.backgroundColor}}}
                            sx={{width: style.leftNav.width}}
                        >
                            <ClickAwayListener onClickAway={closeLeftNavDrawer}>
                                <div>
                                    <ArgoAppBarHook
                                        showMenuArrowButton="LEFT"
                                        onLeftIconButtonTouchTap={toggleLeftNavDrawer}
                                        isDrawer={true}
                                        width={style.leftNav.width}
                                        static={true}
                                        leftNavState={leftNavDrawerOpen}
                                    />
                                    <div style={{position: "fixed", top: "75px", width: style.leftNav.width}}>
                                        <ArgoLeftNavLinkHook linkTo="/questionnaires" title="Questionnaires"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/categories" title="Categories"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/groups" title="Groups"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/questions" title="Questions"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/actions" title="Actions"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/search-tags" title="Tags"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/integrations" title="Integrations"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook linkTo="/import" title="Import Questionnaire"
                                                             onClick={closeLeftNavDrawer}/>
                                        <ArgoLeftNavLinkHook title="Help" onClick={openHelpTab}/>
                                        <ArgoLeftNavLinkHook title="About" onClick={openAboutDialog}/>
                                    </div>
                                    <div style={{position: "fixed", bottom: "0", width: style.leftNav.width}}>
                                        <Divider/>
                                        <ArgoLeftNavLinkHook onClick={handleLogout} title="Log out"/>
                                    </div>
                                </div>
                            </ClickAwayListener>
                        </Drawer> : "" }

                    <ArgoAboutDialogHook fileName="App" open={aboutDialogOpen} close={closeAboutDialog} backendVersionInfo={backendVersionInfo}/>
                </div>
            }

            <Outlet/>

        </div>

    );
};

App.propTypes = {
    location: PropTypes.object,
    portalMode: PropTypes.string
};

export default App;























// import React from "react";
// import Toolbar from "@mui/material//Toolbar";
// import AppBar from "@mui/material//AppBar";
// import Typography from "@mui/material//Typography";
// import IconButton from "@mui/material//IconButton";
// import Button from "@mui/material//Button";
// import MenuIcon from "@mui/material/Menu";
//
// const App = () => {
//   return (
//       <div>
//         <AppBar position="static">
//           <Toolbar>
//             <IconButton
//                 edge="start"
//                 style={{
//                   marginRight: 20,
//                 }}
//                 color="primary"
//                 aria-label="menu"
//             >
//               <MenuIcon />
//             </IconButton>
//             <Typography
//                 variant="h6"
//                 style={{
//                   flexGrow: 1,
//                 }}
//             >
//               Dashboard
//             </Typography>
//             <Button color="inherit">Logout</Button>
//           </Toolbar>
//         </AppBar>
//       </div>
//   );
// };
//
// export default App;





// import logo from './logo.svg';
// import './App.css';
//
//
// function App() {
//   return (
//     <div className="App">
//       <header className="App-header">
//         <img src={logo} className="App-logo" alt="logo" />
//         <p>
//           Edit <code>src/App.js</code> and save to reload.
//         </p>
//         <a
//           className="App-link"
//           href="https://reactjs.org"
//           target="_blank"
//           rel="noopener noreferrer"
//         >
//           Learn React
//         </a>
//       </header>
//     </div>
//   );
// }
//
// export default App;